import Service from 'core/servicev2'
import type { AxiosError } from 'axios'

class TimesheetService extends Service {
  getSites(params?: any): Promise<any> {
    try {
      return this.get('/master/sites', params)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  getEquipments(params?: any): Promise<any> {
    try {
      return this.get('/equipments', params)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  getWorkOrders(params?: any): Promise<any> {
    try {
      return this.get('/workorders', params)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  getWorkOrderById(projectId: string, companyId: string, workOrderId: string): Promise<any> {
    try {
      return this.get(`/workorders(ProjectId='${projectId}',CompanyId='${companyId}')`, { 
        $expand: 'Equipment' 
      })
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  getEquipmentById(projectId: string, companyId: string, equipmentId: string): Promise<any> {
    try {
      return this.get(`/equipments(ProjectId='${projectId}',CompanyId='${companyId}')`, { 
        $expand: 'Site' 
      })
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  // Tasks and Projects
  getTasks(params?: any): Promise<any> {
    try {
      return this.get('/tasks', params)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }



  // Master API - Assets (Fleet)
  // params can include $filter for ParentProjectID (SiteId) if needed
  getAssets(params?: any): Promise<any> {
    try {
      return this.get('/master/assets', params)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  // Master API - Work Orders
  // params can include $filter for ParentProjectID (AssetId) if needed
  getWorkOrdersByAsset(params?: any): Promise<any> {
    try {
      return this.get('/master/workorders', params)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  // Master API - Projects (jobs) - Global
  getProjects(params?: any): Promise<any> {
    try {
      return this.get('/master/projects', params)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  // Master API - Projects (Jobs) for a Site
  getProjectsBySite(siteId: string, params?: any): Promise<any> {
    try {
      return this.get(`/master/sites/${siteId}/projects`, params)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  // Master API - Activities for a Project
  // Master API - Activities (Global or Project)
  getActivities(params?: any): Promise<any> {
    try {
      return this.get('/master/activities', params)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  // Master API - Overhead Task Codes (for overhead mode)
  getOverheadTaskCodes(params?: any): Promise<any> {
    try {
      return this.get('/master/overheadtaskcode', params)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  // V2 Timesheet API - Create timesheet with D365 integration
  createTimesheetV2(data: any): Promise<any> {
    try {
      return this.post('/timesheet', data)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  approveTimesheetV2(data: { ids: number[] }): Promise<any> {
    if (data.ids.length === 1) {
      return this.approveWithChanges(data.ids[0], {})
    }
    // Use batch approval API for multiple timesheets
    return this.approveMultipleTimesheets(data.ids)
  }

  // V2 Timesheet API - Approve multiple timesheets in batch
  approveMultipleTimesheets(ids: number[]): Promise<any> {
    try {
      return this.post('/timesheet/approve-multiple', { ids })
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  getTimesheetDetailV2(id: number): Promise<any> {
    try {
      const expand = `TimesheetLines($expand=TimesheetAdditionals),Approver,User($expand=Resource)`
      const select = 'EndTime,Shift,StartDate,StartTime,SubmittedCount,TimesheetHeaderId,TimesheetStatus,UserId'
      return this.get(`/Timesheet(${id})`, { 
        $expand: expand,
        $select: select,
        _t: new Date().getTime()
      })
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  getTimesheetsByDate(date: string, userId: string, excludeId?: number): Promise<any> {
    try {
      const filters = [
        `StartDate eq ${date}`, 
        `UserId eq ${userId}`,
        `(TimesheetStatus eq 1 or TimesheetStatus eq 2)` // Only Submitted (1) or Approved (2)
      ];
      if (excludeId) {
        filters.push(`TimesheetHeaderId ne ${excludeId}`);
      }
      return this.get('/Timesheet', {
        $filter: filters.join(' and '),
        $expand: 'TimesheetLines',
        $select: 'TimesheetHeaderId,StartDate,UserId,TimesheetStatus,TimesheetLines'
      });
    } catch (error) {
      throw (error as AxiosError).response;
    }
  }

  // Get PO lines for contractor by workerId and companyId
  getPOLines(workerId: string, companyId?: string): Promise<any> {
    try {
      // OData filter syntax: $filter=WorkerId eq 'VALUE' and CompanyId eq 'VALUE'
      let filter = `WorkerId eq '${workerId}'`;
      if (companyId) {
        filter += ` and CompanyId eq '${companyId}'`;
      }
      return this.get('/master/po-lines', { 
        $filter: filter
      })
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  // V2 Timesheet API - Approve timesheet with optional line modifications
  approveWithChanges(id: number, data: {
    shift?: boolean;
    timesheetLines?: any[];
    linesToRemove?: number[];
    poNumber?: string;
    poLineId?: string;
    poLotId?: string;
  }): Promise<any> {
    try {
      return this.post(`/timesheet/${id}/approve`, data)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  // V2 Timesheet API - Update timesheet (Patch)
  updateTimesheet(id: number, data: any): Promise<any> {
    try {
      return this.patch(`/timesheet/${id}`, data) // Uses V2 HttpPatch method
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  // V2 Timesheet API - Recall timesheet
  recallTimesheet(id: number): Promise<any> {
    return this.updateTimesheet(id, {
      TimesheetStatus: 4 // Recalled
    })
  }

  // V2 Timesheet API - Cancel (Void) timesheet
  cancelTimesheet(id: number): Promise<any> {
    return this.updateTimesheet(id, {
      TimesheetStatus: 5 // Cancelled/Void
    })
  }

  // V2 Timesheet API - Decline timesheet with reason
  declineTimesheet(id: number, reason: string): Promise<any> {
    try {
      return this.post(`/timesheet/${id}/decline`, { reason })
    } catch (error) {
      throw (error as AxiosError).response
    }
  }

  getStatusCounts(params?: {
    fromDate?: string;
    toDate?: string;
    timesheetLineType?: number;
    userId?: number;
    searchId?: string;
  }): Promise<any> {
    try {
      return this.get('/TimesheetExpands/status-counts', params)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }


  // V2 Timesheet API - Upload signature image
  uploadSignature(formData: FormData): Promise<any> {
    try {
      return this.postFormData('/Timesheet/UploadSignature', formData)
    } catch (error) {
      throw (error as AxiosError).response
    }
  }
}

export default new TimesheetService()


